import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { posApi } from '@/api/pos.api';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

interface PosApprovalModalProps {
  open: boolean;
  approvalId: number | null;
  title?: string;
  onClose: () => void;
  onApproved: (approvalId: number) => void;
}

export function PosApprovalModal({
  open,
  approvalId,
  title = 'Supervisor approval required',
  onClose,
  onApproved,
}: PosApprovalModalProps) {
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(false);

  const act = async (action: 'APPROVE' | 'REJECT') => {
    if (!approvalId) return;
    setLoading(true);
    try {
      const status = await posApi.actApproval(approvalId, action, comments || undefined);
      if (status === 'APPROVED') {
        notify.success('Approved — retry the action');
        onApproved(approvalId);
        onClose();
      } else {
        notify.error('Request rejected');
        onClose();
      }
    } catch (err) {
      showApiError(err, 'Approval failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="secondary" loading={loading} onClick={() => void act('REJECT')}>
            Reject
          </Button>
          <Button loading={loading} onClick={() => void act('APPROVE')}>
            Approve
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-slate-600 dark:text-slate-300">
        Approval request #{approvalId}. A supervisor must approve before this action can proceed.
      </p>
      <Input
        label="Comments (optional)"
        value={comments}
        onChange={(e) => setComments(e.target.value)}
      />
    </Modal>
  );
}
